import { useMemo, useState } from "react";
import {
  devices as mockDevices,
  deviceStats as mockDeviceStats,
  shipOptions as mockShipOptions,
  statusOptions as mockStatusOptions,
  groupOptions as mockGroupOptions,
} from "@/mocks/devices";
import type { Device } from "@/mocks/devices";
import { useApi } from "@/hooks/useApi";
import { apiSend } from "@/lib/api";
import DeviceDetailDrawer from "./components/DeviceDetailDrawer";
import {
  CreateDeviceModal,
  PairDeviceModal,
  PairingCodesModal,
  type PairingCodeResult,
} from "./components/DeviceModals";
import { confirmDialog, toast } from "@/components/ui/feedback";

const TYPE_CONFIG: Record<string, { label: string; icon: string; color: string }> = {
  glass: { label: "AI眼镜", icon: "ri-glasses-line", color: "bg-teal-50 text-teal-700" },
  phone: { label: "手机", icon: "ri-smartphone-line", color: "bg-sky-50 text-sky-700" },
  tracker: { label: "船载定位器", icon: "ri-ship-2-line", color: "bg-amber-50 text-amber-700" }, // 途强 JT808
};

function DevicesPage() {
  // Live data from the Go API, with bundled mock as fallback while loading /
  // if the backend is unreachable.
  const { data: devices, reload: reloadDevices } = useApi<Device[]>("/devices", mockDevices);
  const { data: deviceStats, reload: reloadStats } = useApi<typeof mockDeviceStats>(
    "/devices/stats",
    mockDeviceStats,
  );
  const { data: options } = useApi<{ ship: string[]; status: typeof mockStatusOptions; group: string[] }>(
    "/devices/options",
    { ship: mockShipOptions, status: mockStatusOptions, group: mockGroupOptions },
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [shipFilter, setShipFilter] = useState("全部船次");
  const [groupFilter, setGroupFilter] = useState("全部分组");
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<Device | null>(null);
  const [pairTarget, setPairTarget] = useState<Device | null>(null);
  const [pairingResults, setPairingResults] = useState<PairingCodeResult[] | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = () => {
    reloadDevices();
    reloadStats();
  };

  const filteredDevices = useMemo(() => {
    return devices.filter((device) => {
      const matchesSearch =
        searchQuery === "" ||
        device.deviceNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
        device.mac.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (device.hardwareId ?? "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (device.boundVisitor && device.boundVisitor.includes(searchQuery));
      const matchesType = typeFilter === "all" || (device.deviceType ?? "glass") === typeFilter;
      const matchesStatus = statusFilter === "all" || device.status === statusFilter;
      const matchesShip = shipFilter === "全部船次" || device.ship === shipFilter;
      const matchesGroup = groupFilter === "全部分组" || device.group === groupFilter;
      return matchesSearch && matchesType && matchesStatus && matchesShip && matchesGroup;
    });
  }, [devices, searchQuery, typeFilter, statusFilter, shipFilter, groupFilter]);

  const groupedDevices = useMemo(() => {
    const groups: Record<string, Device[]> = {};
    filteredDevices.forEach((device) => {
      if (!groups[device.group]) groups[device.group] = [];
      groups[device.group].push(device);
    });
    return groups;
  }, [filteredDevices]);

  const toggleRow = (id: string) => {
    const newSet = new Set(selectedRows);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedRows(newSet);
  };

  const toggleGroup = (groupDevices: Device[]) => {
    const newSet = new Set(selectedRows);
    const allSelected = groupDevices.every((d) => newSet.has(d.id));
    if (allSelected) {
      groupDevices.forEach((d) => newSet.delete(d.id));
    } else {
      groupDevices.forEach((d) => newSet.add(d.id));
    }
    setSelectedRows(newSet);
  };

  // ---- real actions ----

  // 批量预配对: allocate a visitor pairing code for each selected device.
  const batchRequestPairing = async () => {
    if (selectedRows.size === 0) {
      toast("请先勾选需要预配对的设备", "info");
      return;
    }
    setBusy(true);
    try {
      const targets = devices.filter((d) => selectedRows.has(d.id));
      const results: PairingCodeResult[] = [];
      for (const d of targets) {
        const res = await apiSend<{ code: string }>("POST", "/devices/request-pairing", {
          deviceId: d.id,
        });
        results.push({ deviceNo: d.deviceNo, code: res.code });
      }
      setPairingResults(results);
      setSelectedRows(new Set());
      reload();
    } catch (e) {
      toast(`预配对失败: ${e instanceof Error ? e.message : e}`, "error");
    } finally {
      setBusy(false);
    }
  };

  const requestPairingForOne = async (device: Device) => {
    setBusy(true);
    try {
      const res = await apiSend<{ code: string }>("POST", "/devices/request-pairing", {
        deviceId: device.id,
      });
      setPairingResults([{ deviceNo: device.deviceNo, code: res.code }]);
      reload();
    } catch (e) {
      toast(`生成配对码失败: ${e instanceof Error ? e.message : e}`, "error");
    } finally {
      setBusy(false);
    }
  };

  const unbindVisitor = async (device: Device) => {
    if (!(await confirmDialog({ title: "解绑游客", message: `确认解绑 ${device.deviceNo} 的游客「${device.boundVisitor}」？` }))) return;
    setBusy(true);
    try {
      await apiSend("POST", `/devices/${device.id}/unbind`);
      reload();
    } catch (e) {
      toast(`解绑失败: ${e instanceof Error ? e.message : e}`, "error");
    } finally {
      setBusy(false);
    }
  };

  const unpairDevice = async (device: Device) => {
    if (!(await confirmDialog({ title: "解除配对", message: `确认解除 ${device.deviceNo} 与 ${device.pairedDeviceNo} 的配对？` }))) return;
    setBusy(true);
    try {
      await apiSend("POST", `/devices/${device.id}/unpair-device`);
      reload();
    } catch (e) {
      toast(`解除配对失败: ${e instanceof Error ? e.message : e}`, "error");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code).catch(() => {});
  };

  const deleteDevice = async (device: Device) => {
    if (!(await confirmDialog({ title: "删除设备", message: `确认删除设备 ${device.deviceNo}？此操作不可撤销。`, confirmText: "删除" }))) return;
    setBusy(true);
    try {
      await apiSend("DELETE", `/devices/${device.id}`);
      reload();
    } catch (e) {
      toast(`删除失败: ${e instanceof Error ? e.message : e}`, "error");
    } finally {
      setBusy(false);
    }
  };

  const statusConfig = {
    online: { label: "在线", color: "bg-emerald-100 text-emerald-700" },
    weak: { label: "弱连接", color: "bg-amber-100 text-amber-700" },
    offline: { label: "离线", color: "bg-gray-100 text-gray-700" },
    low_battery: { label: "低电", color: "bg-red-100 text-red-700" },
    maintenance: { label: "维护中", color: "bg-purple-100 text-purple-700" },
  };

  const batteryColor = (battery: number) => {
    if (battery > 50) return "bg-emerald-500";
    if (battery > 20) return "bg-amber-500";
    return "bg-red-500";
  };

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">设备管理</h1>
          <p className="text-sm text-gray-500 mt-1">
            设备台账、AI眼镜↔手机配对、游客预配对、状态监控
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={batchRequestPairing}
            disabled={busy}
            className="px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors shadow-sm whitespace-nowrap disabled:opacity-50"
          >
            <i className="ri-link mr-1.5" />
            批量预配对
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2.5 bg-white text-gray-700 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line mr-1.5" />
            录入设备
          </button>
        </div>
      </div>

      {/* Stats Bar: 台账总量 → 真实硬件 → 实时状态。占位 = 台账有编号但
          实机还没开机上报过（hardware 未登记），与空置槽位口径一致。 */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        {[
          { label: "总台账", value: deviceStats.total, sub: `眼镜 ${deviceStats.glass ?? 0} · 手机 ${deviceStats.phone ?? 0}`, color: "text-gray-900", bg: "bg-white", border: "border-gray-200" },
          { label: "已登记硬件", value: deviceStats.registered ?? 0, sub: `占位待入库 ${deviceStats.placeholder ?? 0}`, color: "text-teal-700", bg: "bg-teal-50", border: "border-teal-200" },
          { label: "在线", value: deviceStats.online, sub: "90秒心跳", color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
          { label: "低电", value: deviceStats.lowBattery, sub: "在线且 <20%", color: "text-sky-700", bg: "bg-sky-50", border: "border-sky-200" },
          { label: "在用中", value: deviceStats.borrowed ?? 0, sub: "已开始旅程", color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
          { label: "维护中", value: deviceStats.maintenance, sub: "停用检修", color: "text-purple-700", bg: "bg-purple-50", border: "border-purple-200" },
        ].map((stat) => (
          <div key={stat.label} className={`${stat.bg} border ${stat.border} rounded-lg p-4`}>
            <div className="text-xs text-gray-500 mb-1">{stat.label}</div>
            <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
            <div className="text-[11px] text-gray-400 mt-0.5">{stat.sub}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-56">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索设备号 / 硬件ID / MAC / 游客姓名"
              className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          >
            <option value="all">全部类型</option>
            <option value="glass">AI眼镜</option>
            <option value="phone">手机</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          >
            {options.status.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={shipFilter}
            onChange={(e) => setShipFilter(e.target.value)}
            className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          >
            {options.ship.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          >
            {options.group.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          {(searchQuery || typeFilter !== "all" || statusFilter !== "all" || shipFilter !== "全部船次" || groupFilter !== "全部分组") && (
            <button
              onClick={() => {
                setSearchQuery("");
                setTypeFilter("all");
                setStatusFilter("all");
                setShipFilter("全部船次");
                setGroupFilter("全部分组");
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              重置
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <span className="text-sm text-gray-500">
            共 {filteredDevices.length} 台设备
            {selectedRows.size > 0 && (
              <span className="ml-2 text-teal-600 font-medium">已选 {selectedRows.size} 台</span>
            )}
          </span>
          {selectedRows.size > 0 && (
            <button
              onClick={batchRequestPairing}
              disabled={busy}
              className="px-3 py-1.5 bg-teal-50 text-teal-700 rounded-lg text-xs font-medium hover:bg-teal-100 transition-colors disabled:opacity-50"
            >
              批量预配对 ({selectedRows.size})
            </button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={filteredDevices.length > 0 && filteredDevices.every((d) => selectedRows.has(d.id))}
                    onChange={() => {
                      const newSet = new Set(selectedRows);
                      const allSelected = filteredDevices.every((d) => newSet.has(d.id));
                      if (allSelected) {
                        filteredDevices.forEach((d) => newSet.delete(d.id));
                      } else {
                        filteredDevices.forEach((d) => newSet.add(d.id));
                      }
                      setSelectedRows(newSet);
                    }}
                    className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">设备号</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">类型</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">型号 / 硬件ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">电量</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">配对设备</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">绑定游客/行程</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">配对码</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">最近在线</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {Object.entries(groupedDevices).map(([group, groupDevices]) => (
                <DeviceGroup
                  key={group}
                  group={group}
                  groupDevices={groupDevices}
                  selectedRows={selectedRows}
                  toggleGroup={toggleGroup}
                  toggleRow={toggleRow}
                  statusConfig={statusConfig}
                  batteryColor={batteryColor}
                  onView={setSelectedDevice}
                  onEdit={setEditTarget}
                  onDelete={deleteDevice}
                  onPair={setPairTarget}
                  onUnpair={unpairDevice}
                  onUnbind={unbindVisitor}
                  onRequestCode={requestPairingForOne}
                  onCopyCode={copyCode}
                />
              ))}
            </tbody>
          </table>
          {filteredDevices.length === 0 && (
            <div className="py-16 text-center text-sm text-gray-400">
              <i className="ri-glasses-line text-3xl block mb-2" />
              没有匹配的设备
            </div>
          )}
        </div>
      </div>

      {/* Detail Drawer */}
      {selectedDevice && (
        <DeviceDetailDrawer
          device={selectedDevice}
          onClose={() => setSelectedDevice(null)}
        />
      )}

      {/* Modals */}
      {showCreate && (
        <CreateDeviceModal
          shipOptions={options.ship}
          groupOptions={options.group}
          onClose={() => setShowCreate(false)}
          onCreated={reload}
        />
      )}
      {editTarget && (
        <CreateDeviceModal
          device={editTarget}
          shipOptions={options.ship}
          groupOptions={options.group}
          onClose={() => setEditTarget(null)}
          onCreated={reload}
        />
      )}
      {pairTarget && (
        <PairDeviceModal
          device={pairTarget}
          candidates={devices}
          onClose={() => setPairTarget(null)}
          onPaired={reload}
        />
      )}
      {pairingResults && (
        <PairingCodesModal results={pairingResults} onClose={() => setPairingResults(null)} />
      )}
    </div>
  );
}

// Row group extracted to keep the table readable.
function DeviceGroup({
  group,
  groupDevices,
  selectedRows,
  toggleGroup,
  toggleRow,
  statusConfig,
  batteryColor,
  onView,
  onEdit,
  onDelete,
  onPair,
  onUnpair,
  onUnbind,
  onRequestCode,
  onCopyCode,
}: {
  group: string;
  groupDevices: Device[];
  selectedRows: Set<string>;
  toggleGroup: (devices: Device[]) => void;
  toggleRow: (id: string) => void;
  statusConfig: Record<string, { label: string; color: string }>;
  batteryColor: (battery: number) => string;
  onView: (d: Device) => void;
  onEdit: (d: Device) => void;
  onDelete: (d: Device) => void;
  onPair: (d: Device) => void;
  onUnpair: (d: Device) => void;
  onUnbind: (d: Device) => void;
  onRequestCode: (d: Device) => void;
  onCopyCode: (code: string) => void;
}) {
  return (
    <>
      {/* Group Header */}
      <tr className="bg-gray-50/80">
        <td className="px-4 py-2">
          <input
            type="checkbox"
            checked={groupDevices.every((d) => selectedRows.has(d.id))}
            onChange={() => toggleGroup(groupDevices)}
            className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
          />
        </td>
        <td colSpan={10} className="px-4 py-2">
          <span className="text-sm font-medium text-gray-700">
            <i className="ri-folder-line mr-1.5 text-gray-400" />
            {group}
            <span className="ml-2 text-xs text-gray-400 font-normal">({groupDevices.length} 台)</span>
          </span>
        </td>
      </tr>
      {groupDevices.map((device) => {
        const status = statusConfig[device.status] ?? statusConfig.offline;
        const type = TYPE_CONFIG[device.deviceType] ?? TYPE_CONFIG.glass; // 未知类型兜底,别让整页崩
        return (
          <tr
            key={device.id}
            className="hover:bg-teal-50/30 transition-colors cursor-pointer border-l-[3px] border-transparent hover:border-teal-500"
            onClick={() => onView(device)}
          >
            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={selectedRows.has(device.id)}
                onChange={() => toggleRow(device.id)}
                className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
            </td>
            <td className="px-4 py-3">
              <span className="text-sm font-medium text-gray-900 font-mono whitespace-nowrap">{device.deviceNo}</span>
            </td>
            <td className="px-4 py-3">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${type.color}`}>
                <i className={type.icon} />
                {type.label}
              </span>
            </td>
            <td className="px-4 py-3">
              <div className="text-sm text-gray-700">{device.model || "-"}</div>
              <div className="text-xs text-gray-400 font-mono">{device.hardwareId || device.mac || "-"}</div>
            </td>
            <td className="px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${batteryColor(device.battery)}`}
                    style={{ width: `${device.battery}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500">{device.battery}%</span>
              </div>
            </td>
            <td className="px-4 py-3">
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${status.color}`}>
                {status.label}
              </span>
            </td>
            <td className="px-4 py-3">
              {device.pairedDeviceNo ? (
                <span className="text-sm text-gray-700 font-mono whitespace-nowrap">
                  <i className="ri-link mr-1 text-teal-500" />
                  {device.pairedDeviceNo}
                </span>
              ) : (
                <span className="text-sm text-gray-400">未配对</span>
              )}
            </td>
            <td className="px-4 py-3">
              {device.boundVisitor ? (
                <div className="whitespace-nowrap">
                  <span className="text-sm text-gray-900">{device.boundVisitor}</span>
                  <span className="text-xs text-gray-400 ml-1">({device.trip})</span>
                </div>
              ) : (
                <span className="text-sm text-gray-400">未绑定</span>
              )}
            </td>
            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
              {device.pairingCode ? (
                <div className="flex items-center gap-1">
                  <span className="text-sm font-mono text-gray-700">{device.pairingCode}</span>
                  <button
                    onClick={() => onCopyCode(device.pairingCode!)}
                    className="text-gray-400 hover:text-teal-600 transition-colors"
                    title="复制配对码"
                  >
                    <i className="ri-file-copy-line text-xs" />
                  </button>
                </div>
              ) : (
                <span className="text-sm text-gray-400">-</span>
              )}
            </td>
            <td className="px-4 py-3">
              <span className="text-sm text-gray-500 whitespace-nowrap">{device.lastOnline.split(" ")[1] ?? device.lastOnline}</span>
            </td>
            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => onView(device)}
                  title="查看详情"
                  className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                >
                  <i className="ri-eye-line" />
                </button>
                <button
                  onClick={() => onEdit(device)}
                  title="编辑设备"
                  className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                >
                  <i className="ri-edit-line" />
                </button>
                {device.pairedDeviceNo ? (
                  <button
                    onClick={() => onUnpair(device)}
                    title={`解除与 ${device.pairedDeviceNo} 的配对`}
                    className="w-7 h-7 flex items-center justify-center text-teal-500 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                  >
                    <i className="ri-link-unlink-m" />
                  </button>
                ) : (
                  <button
                    onClick={() => onPair(device)}
                    title="配对设备（眼镜↔手机）"
                    className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                  >
                    <i className="ri-link" />
                  </button>
                )}
                <button
                  onClick={() => onRequestCode(device)}
                  title="生成游客配对码"
                  className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                >
                  <i className="ri-qr-code-line" />
                </button>
                {device.boundVisitor && (
                  <button
                    onClick={() => onUnbind(device)}
                    title="解绑游客"
                    className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <i className="ri-user-unfollow-line" />
                  </button>
                )}
                <button
                  onClick={() => onDelete(device)}
                  title="删除设备"
                  className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                >
                  <i className="ri-delete-bin-line" />
                </button>
              </div>
            </td>
          </tr>
        );
      })}
    </>
  );
}

export default DevicesPage;
